import type { CrmFieldInstances, TableColumnType, TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { buildCrmChildRowId } from 'components/editor/grid/reduxStore/crmRowIds';
import type { VariableNode } from 'services/entities/VariablesEntity';
import { perColumnKey } from '../crmArrayPaste/deriveCrmRows';
import type { CrmBindingFrame } from './crmChipBindings';
import { readChipTemplates, stampChipBindings } from './crmChipBindings';

// Data for one real CRM instance: its objectId, the chip->objectId frame used to stamp it,
// and its own field values. Saved on the pattern row as crmFieldInstances so editing the
// pattern row's cell later can still update the generated child rows correctly.
type InstanceBinding = {
  objectId: string;
  frame: CrmBindingFrame;
  leaves: Record<string, VariableNode>;
};

// Wipe-and-regenerate strategy keeps pattern rows intact while stamping each instance's chips
// with its objectId, since name alone repeats across instances.
// Known gap: a row with chips from two different CRM arrays is only expanded correctly for
// whichever array runs its deriveCrmRows pass first — the second array's chips never get
// stamped on the rows the first array generates. To be handled separately.

export type DeriveCrmRowsParams = {
  rows: TableRowType[];
  columns: TableColumnType[];
  arrayNode: VariableNode;
  isLocked?: boolean;
};

export function isGroupedArrayNode(node: VariableNode): boolean {
  return node.shape?.type === 'array' && node.shape.grouped === true;
}

export function chipBelongsToArray(chipTemplate: string, arrayNode: VariableNode): boolean {
  return Boolean(arrayNode.template) && chipTemplate.startsWith(`${arrayNode.template}.`);
}

function buildInstanceFrames(arrayNode: VariableNode, chipTemplates: ReadonlySet<string>): InstanceBinding[] {
  const frames: InstanceBinding[] = [];
  const chipTemplateList = Array.from(chipTemplates);

  (arrayNode.variables ?? []).forEach((instance) => {
    if (instance.objectId === undefined) return;
    const instanceObjectId = instance.objectId;

    const ownFrame: Record<string, string> = {};
    const ownLeaves: Record<string, VariableNode> = {};
    const nestedArrays: VariableNode[] = [];

    // Canonical structure requires custom walk: stop at grouped arrays (queued for recursive
    // buildInstanceFrames), only map non-array children to objectId. Generic traverseTree would
    // visit instances and corrupt frame mapping.
    function walk(node: VariableNode): void {
      (node.variables ?? []).forEach((child) => {
        if (isGroupedArrayNode(child)) {
          nestedArrays.push(child);
          return;
        }
        if (child.template) {
          ownFrame[child.template] = instanceObjectId;
          if (chipTemplates.has(child.template)) {
            ownLeaves[child.template] = child;
          }
        }
        walk(child);
      });
    }
    walk(instance);

    const referenced = nestedArrays.filter((nested) => chipTemplateList.some((template) => chipBelongsToArray(template, nested)));

    if (referenced.length === 0) {
      frames.push({ objectId: instanceObjectId, frame: ownFrame, leaves: ownLeaves });
      return;
    }

    const [firstReferenced] = referenced;
    buildInstanceFrames(firstReferenced, chipTemplates).forEach((child) => {
      frames.push({
        objectId: child.objectId,
        frame: { ...ownFrame, ...child.frame },
        leaves: { ...ownLeaves, ...child.leaves },
      });
    });
  });

  return frames;
}

export type ChipTemplateRef = { template: string; columnField: string };

export function collectRowChipTemplates(row: TableRowType, columns: TableColumnType[]): ChipTemplateRef[] {
  const refs: ChipTemplateRef[] = [];
  columns.forEach((column) => {
    readChipTemplates(row[column.field]).forEach((template) => {
      refs.push({ template, columnField: column.field });
    });
  });
  return refs;
}

// Which of `candidateArrayNodes` does this table's rows actually reference via a CRM
// chip — one deriveCrmRows call is needed per entry returned here.
export function findReferencedArrayNodes(
  rows: TableRowType[],
  columns: TableColumnType[],
  candidateArrayNodes: VariableNode[]
): VariableNode[] {
  const chipTemplates = new Set<string>();
  rows.forEach((row) => {
    collectRowChipTemplates(row, columns).forEach((ref) => chipTemplates.add(ref.template));
  });
  if (chipTemplates.size === 0) return [];

  const chipTemplateList = Array.from(chipTemplates);
  return candidateArrayNodes.filter((arrayNode) => chipTemplateList.some((template) => chipBelongsToArray(template, arrayNode)));
}

export function pruneRowsForUnlinkedArrays(
  rows: TableRowType[],
  columns: TableColumnType[],
  linkedArrayNodes: VariableNode[]
): TableRowType[] {
  const orphanedPatternRowIds = new Set(
    rows
      .filter((row) => row.crmRowKind === undefined)
      .filter((row) => {
        const chipTemplates = collectRowChipTemplates(row, columns);
        if (chipTemplates.length === 0) return false;
        return chipTemplates.every((ref) => !linkedArrayNodes.some((arrayNode) => chipBelongsToArray(ref.template, arrayNode)));
      })
      .map((row) => row.id)
  );

  if (orphanedPatternRowIds.size === 0) return rows;

  return rows.filter(
    (row) => !(row.crmRowKind === 'auto-generated' && row.crmSourceRowId !== undefined && orphanedPatternRowIds.has(row.crmSourceRowId))
  );
}

// Saved on the row so editing a cell later can still look up each field's real values.
// Keyed by perColumnKey(columnField, template), matching crmArrayPaste/deriveCrmRows.ts —
// one entry per column that actually carries a chip for that template.
function buildCrmFieldInstances(bindings: InstanceBinding[], chipTemplateRefs: ChipTemplateRef[]): CrmFieldInstances {
  const byKey: CrmFieldInstances = {};
  bindings.forEach(({ leaves }) => {
    Object.values(leaves).forEach((leaf) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const template = leaf.template!;
      const columnFields = chipTemplateRefs.filter((ref) => ref.template === template).map((ref) => ref.columnField);
      columnFields.forEach((columnField) => {
        const key = perColumnKey(columnField, template);
        const list = byKey[key] ?? [];
        list.push({
          name: leaf.name,
          friendlyName: leaf.friendlyName ?? undefined,
          value: leaf.value,
          objectId: leaf.objectId,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          template: leaf.template!,
        });
        byKey[key] = list;
      });
    });
  });
  return byKey;
}

function stampRow(row: TableRowType, columns: TableColumnType[], frame: CrmBindingFrame, arrayNode: VariableNode): TableRowType {
  const stamped: TableRowType = { ...row };
  columns.forEach((column) => {
    const content = row[column.field];
    if (typeof content === 'string') {
      stamped[column.field] = stampChipBindings(content, frame, (chipName) => chipBelongsToArray(chipName, arrayNode));
    }
  });
  return stamped;
}

export function deriveCrmRows(params: DeriveCrmRowsParams): TableRowType[] {
  const { rows, columns, arrayNode, isLocked = false } = params;

  if (isLocked) return rows;
  if (!isGroupedArrayNode(arrayNode)) return rows;

  const participatingChipTemplatesByRowId = new Map<string, ChipTemplateRef[]>();
  rows.forEach((row) => {
    if (row.crmRowKind !== undefined) return;
    const chipTemplates = collectRowChipTemplates(row, columns);
    if (chipTemplates.some((ref) => chipBelongsToArray(ref.template, arrayNode))) {
      participatingChipTemplatesByRowId.set(row.id, chipTemplates);
    }
  });

  const existingRowIds = new Set(rows.map((row) => row.id));
  const survivors = rows.filter((row) => {
    if (row.crmRowKind !== 'auto-generated') return true;

    if (row.crmSourceRowId === undefined || !existingRowIds.has(row.crmSourceRowId)) {
      return false;
    }
    return !participatingChipTemplatesByRowId.has(row.crmSourceRowId);
  });

  const result: TableRowType[] = [];

  survivors.forEach((row) => {
    const chipTemplates = participatingChipTemplatesByRowId.get(row.id);

    if (chipTemplates === undefined) {
      result.push(row);
      return;
    }

    const bindings = buildInstanceFrames(arrayNode, new Set(chipTemplates.map((ref) => ref.template)));

    if (bindings.length === 0) {
      result.push(stampRow(row, columns, {}, arrayNode));
      return;
    }

    const crmFieldInstances = buildCrmFieldInstances(bindings, chipTemplates);

    result.push({
      ...stampRow(row, columns, bindings[0].frame, arrayNode),
      crmFieldInstances,
      crmInstanceObjectId: bindings[0].objectId,
    });

    for (let index = 1; index < bindings.length; index++) {
      const { crmFieldInstances: _crmFieldInstances, ...stampedCells } = stampRow(row, columns, bindings[index].frame, arrayNode);
      result.push({
        ...stampedCells,
        id: buildCrmChildRowId(row.id, index),
        crmSourceRowId: row.id,
        crmInstanceObjectId: bindings[index].objectId,
        crmRowKind: 'auto-generated',
      });
    }
  });

  return result;
}
